import AudioPlayer from 'components/AudioPlayer'
import { CaptionDropdown } from 'components/CaptionDropdown'
import { srtGenerator } from 'util/transcript/srt-generator'
import { textSegmentation } from 'util/transcript/text-segmentation'
import { vttGenerator } from 'util/transcript/vtt-generator'
import { FormattedExport } from './FormattedExport'
import { Submission } from 'lib/types/generated-types'
import { Transcript } from 'interfaces/db/transcript'
import { useMemo, useState } from 'react'
import Marquee from 'react-fast-marquee'
import { useAudioStore } from './useAudioStore'

interface TranscriptHeaderProps {
  submission: Submission
  transcript?: Transcript
  hasExportOptions?: boolean
}

export const TranscriptHeader = ({
  submission,
  transcript,
  hasExportOptions = true,
}: TranscriptHeaderProps) => {
  const handleSaveSrt = () => {
    if (!transcript) return
    const segmentedSentences = textSegmentation(transcript.sentences)
    return srtGenerator(segmentedSentences)
  }

  const handleSaveVtt = () => {
    if (!transcript || !transcript.sentences) return
    const segmentedSentences = textSegmentation(transcript.sentences)
    return vttGenerator(segmentedSentences)
  }

  const handleSaveSrtWord = () => {
    if (!transcript || !transcript.sentences) return
    const words = transcript.sentences.flatMap((sentence) => sentence.words)
    return srtGenerator(words)
  }

  if (!transcript) return null

  return (
    <div className="z-10 flex w-full flex-col gap-x-3 gap-y-2 rounded-lg border border-slate-200 bg-white py-2 px-4 sm:flex-row md:items-center md:justify-end md:gap-y-0 md:py-0 lg:justify-between">
      <FileDescription submission={submission} transcript={transcript} />
      <div className="flex h-full w-full flex-col items-center justify-center">
        <AudioPlayer />
      </div>
      {hasExportOptions && (
        <div className="flex h-full flex-col justify-center py-2 md:items-center">
          <p className="mb-2 w-full text-left text-xs font-light">
            Export Options
          </p>
          <div className="flex items-center gap-3 md:justify-between">
            <CaptionDropdown
              srtFunction={handleSaveSrt}
              vttFunction={handleSaveVtt}
              srtWordFunction={handleSaveSrtWord}
              filename={submission.filename}
            />
            <FormattedExport
              submission_id={submission?.id}
              transcript={transcript}
            />
          </div>
        </div>
      )}
    </div>
  )
}

interface FileDescriptionProps {
  submission: Submission
  transcript: Transcript
}

const FileDescription = ({ submission, transcript }: FileDescriptionProps) => {
  const { isPlaying } = useAudioStore()
  const [isHovering, setIsHovering] = useState(false)
  const [isHoveringSpeaker, setIsHoveringSpeaker] = useState(false)

  const speakerNames = useMemo(() => {
    return transcript.sentences
      .map((s) => s.speaker)
      .filter((value, index, self) => self.indexOf(value) === index)
      .join(', ')
  }, [transcript])

  return (
    <div className="flex flex-col items-start">
      <div
        className="relative w-full overflow-hidden md:w-[160px] lg:w-[240px]"
        style={{ height: '24px' }}
      >
        <div
          className="h-full w-full"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {isHovering || isPlaying ? (
            <Marquee
              speed={20}
              play={isHovering || isPlaying}
              className="w-full"
            >
              {submission.title || submission.filename}
              <span className="text-transparent">.....</span>
            </Marquee>
          ) : (
            <div className="w-full overflow-hidden text-ellipsis whitespace-nowrap">
              {submission.title || submission.filename}
              <span className="text-transparent">.....</span>
            </div>
          )}
        </div>
      </div>
      <div
        className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs text-gray-500 md:w-[160px] lg:w-[240px]"
        onMouseEnter={() => setIsHoveringSpeaker(true)}
        onMouseLeave={() => setIsHoveringSpeaker(false)}
      >
        {isHoveringSpeaker ? (
          <Marquee speed={20} play={isHoveringSpeaker} className="w-full">
            {speakerNames}
            <span className="text-transparent">.....</span>
          </Marquee>
        ) : (
          speakerNames
        )}
      </div>
    </div>
  )
}
